import React, { createContext, useContext, useReducer } from "react";

/**
 * Initial state for country context
 */
const initialState = {
  countries: [],
  selectedActor1: null,
  selectedActor2: null,
  loading: false,
  error: null,
};

/**
 * Action types for country reducer
 */
const ActionTypes = {
  SET_COUNTRIES: "SET_COUNTRIES",
  SET_ACTOR1: "SET_ACTOR1",
  SET_ACTOR2: "SET_ACTOR2",
  SWAP_ACTORS: "SWAP_ACTORS",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
  RESET_SELECTION: "RESET_SELECTION",
};

/**
 * Reducer function for country state management
 * @param {Object} state - Current state
 * @param {Object} action - Action object with type and payload
 * @returns {Object} New state
 */
function countryReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_COUNTRIES:
      return {
        ...state,
        countries: action.payload,
        loading: false,
        error: null,
      };

    case ActionTypes.SET_ACTOR1:
      return {
        ...state,
        selectedActor1: action.payload,
        error: null,
      };

    case ActionTypes.SET_ACTOR2:
      return {
        ...state,
        selectedActor2: action.payload,
        error: null,
      };

    case ActionTypes.SWAP_ACTORS:
      return {
        ...state,
        selectedActor1: state.selectedActor2,
        selectedActor2: state.selectedActor1,
        error: null,
      };

    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };

    case ActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case ActionTypes.RESET_SELECTION:
      return {
        ...state,
        selectedActor1: null,
        selectedActor2: null,
        error: null,
      };

    default:
      return state;
  }
}

/**
 * Country context
 */
const CountryContext = createContext();

/**
 * Country context provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export function CountryProvider({ children }) {
  const [state, dispatch] = useReducer(countryReducer, initialState);

  /**
   * Set countries list
   * @param {Array} countries - Array of country objects
   */
  const setCountries = (countries) => {
    dispatch({ type: ActionTypes.SET_COUNTRIES, payload: countries });
  };

  /**
   * Set selected actor1 (source country)
   * @param {Object} country - Country object
   */
  const setActor1 = (country) => {
    dispatch({ type: ActionTypes.SET_ACTOR1, payload: country });
  };

  /**
   * Set selected actor2 (target country)
   * @param {Object} country - Country object
   */
  const setActor2 = (country) => {
    dispatch({ type: ActionTypes.SET_ACTOR2, payload: country });
  };

  /**
   * Swap actor1 and actor2 positions
   */
  const swapActors = () => {
    dispatch({ type: ActionTypes.SWAP_ACTORS });
  };

  /**
   * Set loading state
   * @param {boolean} loading - Loading state
   */
  const setLoading = (loading) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: loading });
  };

  /**
   * Set error state
   * @param {string|null} error - Error message
   */
  const setError = (error) => {
    dispatch({ type: ActionTypes.SET_ERROR, payload: error });
  };

  /**
   * Clear error state
   */
  const clearError = () => {
    dispatch({ type: ActionTypes.CLEAR_ERROR });
  };

  /**
   * Reset country selection
   */
  const resetSelection = () => {
    dispatch({ type: ActionTypes.RESET_SELECTION });
  };

  const value = {
    ...state,
    setCountries,
    setActor1,
    setActor2,
    swapActors,
    setLoading,
    setError,
    clearError,
    resetSelection,
  };

  return (
    <CountryContext.Provider value={value}>{children}</CountryContext.Provider>
  );
}

/**
 * Hook to use country context
 * @returns {Object} Country context value
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useCountryContext() {
  const context = useContext(CountryContext);

  if (!context) {
    throw new Error("useCountryContext must be used within a CountryProvider");
  }

  return context;
}